/**
 * Shared helper: resolve artist IDs against globe points and push into the shot queue.
 */

import { toast } from "sonner";
import type { GlobePoint } from "#shared/schemas/globe.ts";
import { resolveArtistIdsToTargets } from "@web/features/globe/shotTargets.ts";
import { useGlobeShotStore } from "@web/stores/globeShot.ts";

export function enqueueShotArtists(
  artistIds: string[],
  artistNames: Map<string, string>,
  points: GlobePoint[],
): void {
  if (artistIds.length === 0) {
    toast.message("Nothing to add");
    return;
  }
  const { targets, skipped } = resolveArtistIdsToTargets(
    artistIds,
    artistNames,
    points,
  );
  useGlobeShotStore.getState().addTargets(targets, skipped);
  useGlobeShotStore.getState().setQueueCollapsed(false);
  toast.success(
    `Added ${targets.length} to shot queue` +
      (skipped.length ? ` · ${skipped.length} skipped` : ""),
  );
}

/** Collect artist IDs from Ideas candidates (entity artist or credited artists). */
export function artistIdsFromIdeaCandidates(
  candidates: Array<{
    entity_type: string;
    entity_id: string;
    artists: Array<{ id: string; name: string | null }>;
  }>,
): { ids: string[]; names: Map<string, string> } {
  const ids: string[] = [];
  const names = new Map<string, string>();
  for (const c of candidates) {
    if (c.entity_type === "artist") {
      ids.push(c.entity_id);
      const n = c.artists[0]?.name;
      if (n) names.set(c.entity_id, n);
    } else {
      for (const a of c.artists) {
        ids.push(a.id);
        if (a.name) names.set(a.id, a.name);
      }
    }
  }
  return { ids, names };
}
